import { Component, effect, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CreateUpdatePaymentLineDto,
  CustomerDeliveryNoteService,
  DayBookService,
  DocPaymentNature,
} from '@digiwall/goliath-documents/proxy';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { combineLatest, concatMap, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { PaymentMethodDto } from '@digiwall/goliath-contacts/proxy';
import { toObservable } from '@angular/core/rxjs-interop';
import { PaymentMethodTypeStore } from '@digiwall/goliath-contacts';
import { DisplayNamePipe, ErrorFormControlDirective } from '@digiwall/core';
import { LocalizationModule } from '@abp/ng.core';
import { FileUrlPipe } from '@digiwall/file-management';
import { NgxMaskDirective } from 'ngx-mask';

@Component({
  selector: 'document-payment-side-panel',
  templateUrl: './doc-payment-side-panel.component.html',
  styleUrl: './doc-payment-side-panel.component.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorFormControlDirective,
    AsyncPipe,
    LocalizationModule,
    NgxMaskDirective,
    DisplayNamePipe,
    FileUrlPipe,
  ],
})
export class DocPaymentSidePanelComponent implements OnInit, OnDestroy {
  invoiceId = input<string>(null);
  originalRemainingAmount = input<number>(0);
  originalDiscount = input<number>(0);
  onPaymentAdded = output<number>();

  paymentMethodEnumerationTypeStore = inject(PaymentMethodTypeStore);

  private destroy$ = new Subject<void>();

  PaymentNature = DocPaymentNature;
  hasPayments = false;

  paymentMethods$: Observable<PaymentMethodDto[]>;
  filteredPaymentMethods: PaymentMethodDto[] = [];

  private _remainingAmount = 0;
  private set remainingAmount(value: number) {
    this._remainingAmount = value < 0 ? 0 : value;
  }
  protected get remainingAmount(): number {
    return this._remainingAmount;
  }

  form: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private service: CustomerDeliveryNoteService,
    private dayBookService: DayBookService,
    private toasterService: ToasterService,
    private confirmationService: ConfirmationService,
  ) {
    this.initForm();

    this.paymentMethods$ = toObservable(this.paymentMethodEnumerationTypeStore.entities);
    effect(() => {
      this.remainingAmount = this.originalRemainingAmount();
      this.form.get('discount').setValue(this.originalDiscount());

      this.service.hasActivePayment(this.invoiceId()).subscribe(hasPayments => {
        this.hasPayments = hasPayments;
      });
    });
  }

  ngOnInit(): void {
    combineLatest([this.dayBookService.getCurrentSalesSlipPaymentMethods(), this.paymentMethods$])
      .pipe(
        map(([currentPaymentMethodIds, paymentMethods]) => {
          return paymentMethods.filter(paymentMethod =>
            currentPaymentMethodIds.includes(paymentMethod.id),
          );
        }),
      )
      .subscribe(paymentMethods => {
        this.filteredPaymentMethods = paymentMethods;
        this.setPaymentMethodControls();
      });
  }

  initForm() {
    this.form = this.formBuilder.group({
      paymentMethods: this.formBuilder.array([]),
      discount: [null, [Validators.min(0), Validators.max(100)]],
    });

    this.form
      .get('discount')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.discountChanged();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get paymentMethods(): FormArray {
    return this.form.get('paymentMethods') as FormArray;
  }

  setPaymentMethodControls() {
    this.paymentMethods.clear();

    this.filteredPaymentMethods.forEach(paymentMethod => {
      let paymentMethodControl = this.formBuilder.control(paymentMethod.id, [Validators.required]);
      let amountControl = this.formBuilder.control(null);
      paymentMethodControl.disable();
      amountControl.disable();

      this.paymentMethods.controls.push(
        this.formBuilder.group({
          selected: [false],
          paymentMethodId: paymentMethodControl,
          amount: amountControl,
          logo: paymentMethod.logo,
        }),
      );
    });
  }

  selectPayment(paymentControl: AbstractControl, paymentInputRef: HTMLInputElement) {
    let selectedControl = paymentControl.get('selected');
    let amountControl = paymentControl.get('amount');

    if (selectedControl != null && amountControl != null) {
      selectedControl.patchValue(!selectedControl.value);

      selectedControl.value ? amountControl.enable() : amountControl.disable();

      amountControl.patchValue(selectedControl.value ? this.remainingAmount : null);

      this.computeRemainingAmountAmount();

      if (selectedControl.value) {
        paymentInputRef.focus();
        paymentInputRef.select();
      }

      this.form.markAsDirty();
    }
  }

  discountChanged(): void {
    this.computeRemainingAmountAmount();
  }

  paymentAmountChanged(paymentControl: AbstractControl) {
    if (paymentControl.get('amount').value == null) {
      paymentControl.get('selected').patchValue(false);
      paymentControl.get('amount').disable();
    }

    this.computeRemainingAmountAmount();
  }

  private computeRemainingAmountAmount(): void {
    const totalPaid = this.paymentMethods.controls.reduce((sum, control) => {
      return sum + (control.get('amount')?.value ?? 0);
    }, 0);

    const newDiscount = this.form.get('discount')?.value ?? 0;
    const oldDiscount = this.originalDiscount() ?? 0;
    const baseAmount = this.originalRemainingAmount() / (1 - oldDiscount / 100);

    const totalWithNewDiscount = baseAmount * (1 - newDiscount / 100);

    this.remainingAmount = Math.round((totalWithNewDiscount - totalPaid) * 100) / 100;
  }

  save() {
    var discount = this.form.get('discount').value;
    const payementLines = this.paymentMethods.controls
      .map(control => {
        return {
          paymentMethod: control.get('paymentMethodId')?.value,
          amount: control.get('amount')?.value,
        } as CreateUpdatePaymentLineDto;
      })
      .filter(payementLine => payementLine.paymentMethod != null && payementLine.amount);

    if (payementLines.length <= 0) {
      return;
    }

    this.service.addPayment(this.invoiceId(), payementLines).subscribeOnce(_ => {
      this.refreshPayments();
      this.form.markAsPristine();
      this.onPaymentAdded.emit(discount);
    });
  }

  cancelPayment(payementId) {
    this.confirmationService
      .warn('Doc::CustomerInvoice:CancelPayment', '::AreYouSure')
      .pipe(
        concatMap(confirmationStatus => {
          if (confirmationStatus == Confirmation.Status.confirm) {
            return this.service.reversePayment(this.invoiceId(), payementId).pipe(map(() => true));
          }
          return of(null);
        }),
      )
      .subscribe(paymentCancelled => {
        if (paymentCancelled) {
          this.toasterService.success('Doc::CustomerInvoice:PaymentReversed');
          this.refreshPayments();
        }
      });
  }

  private refreshPayments() {
    this.form.reset();
    this.setPaymentMethodControls();
  }
}
